/**
 * AnimatedAvatar — gives any character portrait a subtle "alive" feel:
 *   - very slow chest-breathing scale + drift (4.5s loop)
 *   - rare blink-like brightness pulse (every 5-8s, randomised per mount)
 *
 * Drop-in replacement for a plain <img>. Wrap an img or use src directly.
 *
 * Props:
 *   src         — image url
 *   alt         — accessibility
 *   className   — pass any tailwind classes (e.g. "w-full h-full object-cover")
 *   intensity   — "subtle" | "lively" (defaults to subtle)
 *   rounded     — boolean (default true) — round the container
 *   children    — optional overlay content rendered ABOVE the avatar
 */
import { useEffect, useId, useRef } from 'react';

export default function AnimatedAvatar({
  src,
  alt = '',
  className = '',
  intensity = 'subtle',
  rounded = true,
  children,
}) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '');
  const imgRef = useRef(null);
  const lively = intensity === 'lively';
  // GIFs animate themselves — adding our breath/blink CSS animation on top would clash.
  const isAnimatedFile = !!src && /\.gif(\?|$)/i.test(src);

  useEffect(() => {
    if (isAnimatedFile) return;
    // Randomise the blink offset so multiple avatars don't blink in sync
    const delay = -(Math.random() * 6).toFixed(2);
    const node = imgRef.current;
    if (node) {
      node.style.setProperty('--blink-delay', `${delay}s`);
    }
  }, [isAnimatedFile]);

  const breathDuration = lively ? '3.6s' : '5.2s';
  const breathScale = lively ? 1.03 : 1.018;
  const blinkPeriod = lively ? '5.5s' : '7.5s';

  return (
    <div className={`relative overflow-hidden ${rounded ? 'rounded-full' : ''} animus-avatar-${uid}`}>
      {!isAnimatedFile && (
        <style>{`
          @keyframes animus-breath-${uid} {
            0%, 100% { transform: scale(1) translateY(0); filter: brightness(1); }
            50%      { transform: scale(${breathScale}) translateY(-0.5px); filter: brightness(1.04); }
          }
          @keyframes animus-blink-${uid} {
            0%, 92%, 100% { filter: brightness(1); }
            94%           { filter: brightness(0.7); }
            96%           { filter: brightness(1); }
          }
          .animus-avatar-img-${uid} {
            animation:
              animus-breath-${uid} ${breathDuration} ease-in-out infinite,
              animus-blink-${uid} ${blinkPeriod} ease-in-out infinite;
            animation-delay: 0s, var(--blink-delay, 0s);
            transform-origin: 50% 60%;
            will-change: transform, filter;
          }
          @media (prefers-reduced-motion: reduce) {
            .animus-avatar-img-${uid} { animation: none !important; }
          }
        `}</style>
      )}
      {src ? (
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          loading="lazy"
          className={`${isAnimatedFile ? '' : `animus-avatar-img-${uid}`} ${className}`}
        />
      ) : (
        <div className={`bg-gradient-to-br from-[#1A1D24] to-[#0F1115] ${className}`} />
      )}
      {children}
    </div>
  );
}
